<script setup lang="ts">
import type { VueComponentData } from '@ionic/vue-router/dist/types/types';
import { ref, nextTick, onMounted, watch, computed } from 'vue';

type Tab<T> = {
  label: string;
  value: T;
  component?: VueComponentData;
};

const { tabs, initialTab } = defineProps<{
  tabs: Tab<unknown>[];
  initialTab: unknown | undefined;
}>();

const emit = defineEmits<{
  <T>(e: 'on-tab-selected', tabValue: T): void;
}>();

const selectedTab = ref<unknown | null>(null);
const tabRefs = ref<{ [key: string]: HTMLElement | null }>({});
const underlineStyle = ref({ width: '0px', left: '0px' });

const selectedTabComponent = computed(() => {
  return tabs.find(tab => tab.value === selectedTab.value)?.component || null;
});

onMounted(() => {
  selectedTab.value = initialTab;
});

watch(selectedTab, () => {
  //   Had to cheat death IM SORRY
  setTimeout(updateUnderline);
});

const selectTab = (tabValue: unknown) => {
  if (selectedTab.value === tabValue) return;
  selectedTab.value = tabValue;
  emit('on-tab-selected', tabValue);
};

const updateUnderline = () => {
  nextTick(() => {
    if (selectedTab.value !== null) {
      const selectedTabElement = tabRefs.value[selectedTab.value as string];
      if (selectedTabElement) {
        underlineStyle.value = {
          width: `${selectedTabElement.offsetWidth}px`,
          left: `${selectedTabElement.offsetLeft}px`,
        };
      }
    }
  });
};

const setTabRef = (tabValue: unknown, el: HTMLElement | null) => {
  if (el) {
    tabRefs.value[tabValue as string] = el;
  }
};
</script>

<template>
  <div class="tabs-container overflow-x-auto w-full">
    <ul class="tabs-list inline-flex relative w-full">
      <li
        v-for="(tab, idx) in tabs"
        :key="tab.label + idx"
        :ref="el => setTabRef(tab.value, el as HTMLElement)"
        :class="{ 'active font-bold': selectedTab === tab.value }"
        class="tab-item py-2.5 px-4 text-center text-light text-base"
        @click="selectTab(tab.value)"
      >
        {{ tab.label }}
      </li>
      <!-- Dynamic underline -->
      <div class="underline bg-primary absolute bottom-0" :style="underlineStyle"></div>
    </ul>

    <div class="tabs-content-wrapper" v-if="selectedTabComponent">
      <Transition name="fade">
        <component :is="selectedTabComponent" :key="selectedTab" />
      </Transition>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/scss/setup/_variables.scss';
@import '@/scss/setup/_functions.scss';

.tabs-container {
  white-space: nowrap;
  scrollbar-width: none;
  border-bottom: 1px solid $color-medium-light;

  &::-webkit-scrollbar {
    display: none;
  }

  .tab-item {
    cursor: pointer;
    white-space: nowrap;
  }

  .underline {
    height: toRem(2px);
    transition:
      left 0.3s ease,
      width 0.3s ease;
  }

  .tabs-content-wrapper {
    overflow: hidden;
    position: relative;
    width: 100%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms var(--material-transition-timing-fn);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
